import { useTranslation } from 'react-i18next';
import { useDeepDiveState } from '../context/DeepDiveStateContext';
import { useDebriefFactsState } from '../../shared/context/DebriefFactsStateContext';
import { deriveActiveStep } from '../utils/nextStep';
import { DeepDiveChatHeader } from './DeepDiveChatHeader';
import { TurnList } from './TurnList';
import { ChatInputRow } from './ChatInputRow';

export function DeepDiveChat() {
  const { t } = useTranslation('debriefV2');
  const { state, dispatch } = useDeepDiveState();
  const { factsByStage } = useDebriefFactsState();
  const step = deriveActiveStep(state, factsByStage);

  const totalMatchPoints = state.matchPoints.length;

  if (
    step.isComplete ||
    step.activeMatchPointIndex === null ||
    !step.activeSession ||
    !step.activeTurn
  ) {
    return null;
  }

  const matchPointIndex = step.activeMatchPointIndex;
  const session = step.activeSession;
  const activeTurn = step.activeTurn;

  const inputResetKey = `${matchPointIndex}-${session.turns.length}-${
    step.activeQuestion?.index ?? 'lesson'
  }`;

  const handlePickFact = (factId: string) => {
    dispatch({
      type: 'PICK_FACT',
      payload: { matchPointIndex, factId },
    });
  };

  const handleAnswerSubmit = (answer: string) => {
    dispatch({
      type: 'ANSWER_QUESTION',
      payload: { matchPointIndex, answer },
    });
  };

  const handleLessonSubmit = (lesson: string) => {
    dispatch({
      type: 'ADD_LESSON',
      payload: { matchPointIndex, lesson },
    });
  };

  const handleDone = () => {
    dispatch({
      type: 'COMPLETE_MATCH_POINT',
      payload: { matchPointIndex },
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 w-full max-w-[720px] mx-auto flex flex-col relative overflow-hidden">
      <DeepDiveChatHeader
        stage={session.stage}
        matchPointIndex={matchPointIndex}
        totalMatchPoints={totalMatchPoints}
      />

      <TurnList
        session={session}
        facts={step.activeFacts}
        onPickFact={handlePickFact}
        isFirstBotTurnInChat={matchPointIndex === 0}
      />

      {activeTurn.kind === 'question' && (
        <ChatInputRow
          mode="answer"
          onSubmit={handleAnswerSubmit}
          resetKey={`answer-${inputResetKey}`}
        />
      )}

      {activeTurn.kind === 'lesson' && (
        <ChatInputRow
          mode="lesson"
          onSubmit={handleLessonSubmit}
          onDone={handleDone}
          doneEnabled={activeTurn.lessons.length > 0}
          resetKey={`lesson-${inputResetKey}-${activeTurn.lessons.length}`}
        />
      )}

      {activeTurn.kind === 'fact-pick' && (
        <div className="px-5 py-4 border-t border-slate-200 bg-slate-50/40 rounded-b-2xl text-[13px] text-slate-500">
          {t('deepDive.pickFactHint')}
        </div>
      )}
    </div>
  );
}
